import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { forms } from '@cwos/api-client';
import { Pill, Refused, ScopeNote, SkeletonSet, TableCard, type Column } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { LoadFailure } from '../LoadFailure.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import { INSTRUMENT_READING, SUBJECT_READING, formatWhen } from './formsLogic.js';
import {
  RESPONSE_REFUSED_HEADING,
  RESPONSE_REFUSED_MESSAGE,
  WITHHELD_OR_ABSENT_HEADING,
  WITHHELD_OR_ABSENT_MESSAGE,
  type AnswerRow,
  answerText,
  answersAgainstVersion,
  attestationReading,
  responsePhase,
  responseReachScope,
  responseScopeSentence,
  versionCurrency,
  versionCurrencySentence,
  widenerWithoutDirectoryNote,
} from './formsResponseLogic.js';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * ONE FORM RESPONSE, READ AGAINST THE VERSION IT ANSWERED.
 *
 * ===========================================================================
 * WHY THERE IS A LOOKUP BOX AND NOT A LIST
 * ===========================================================================
 * Because there is no list route, and inventing one on this side would be
 * worse than the gap. `response-reach.ts` says so in its own header: the forms
 * surface has exactly two doors — `POST /api/forms/responses`, which answers
 * with the row the caller just wrote, and `GET /api/forms/responses/:id`. Reach
 * is decided per row at read time and there is deliberately no SQL predicate,
 * so a client-side "list" would have to fetch ids from somewhere and probe
 * them, which is the enumeration the 404 exists to prevent. A reader arrives
 * here with an id — from an incident, a task, a submission receipt or an audit
 * entry — and this screen opens it. That the domain has no list route is a real
 * gap and it is named in this lane's report rather than papered over here.
 *
 * ===========================================================================
 * FIVE STATES, AND THE 404 IS NOT "EMPTY"
 * ===========================================================================
 * `formsResponseLogic.responsePhase` decides. The one this screen exists for is
 * `withheld-or-absent`: the server answers 404 for a response that is not there
 * AND for one that is not yours, with the same constant message, so that a
 * caller trying uuids learns nothing. This screen therefore says both things
 * and picks neither, and it offers no retry, because asking again cannot change
 * an answer the server gave deliberately.
 *
 * ===========================================================================
 * TENANT CONTENT, RENDERED AS TEXT
 * ===========================================================================
 * Every label, option, section title, statement and answer on this page was
 * typed by somebody in the customer's organization. All of it goes through React
 * text nodes. There is no `dangerouslySetInnerHTML` here and there must never
 * be one: a form is the one surface in this product where an attacker gets to
 * choose both the question and the answer.
 */
export function FormResponseReader() {
  const params = useParams({ strict: false }) as { responseId?: string };
  const navigate = useNav();
  const { has } = usePermissions();

  const [typed, setTyped] = useState('');
  const responseId = params.responseId ?? '';
  const asked = responseId !== '';

  const scope = responseReachScope(has);
  const widenerNote = widenerWithoutDirectoryNote(has);

  const detail = useQuery({
    queryKey: ['forms', 'response', responseId],
    queryFn: () => forms.response(responseId),
    enabled: asked,
    // A 404 here is an ANSWER, not a transport hiccup, and three more attempts
    // at it are three more probes of an id the server already declined to
    // discuss. A 403 is an answer too.
    retry: false,
  });

  const phase = responsePhase(detail, asked);

  function open(id: string) {
    const trimmed = id.trim();
    if (trimmed === '') return;
    navigate(`/compliance/forms/responses/${trimmed}`);
  }

  return (
    <div className="rn-col" style={{ maxWidth: 1000 }}>
      <SettingsHeader
        title="Open a response"
        blurb="A response is evidence. It answers one version of one form, and Phibian shows you that version — the wording the person was actually given — rather than whatever the form says today."
      />

      <div>
        <button
          type="button"
          className="rn-btn-quiet"
          onClick={() => navigate('/compliance/forms')}
        >
          {'‹'} All forms
        </button>
      </div>

      <SettingsCard>
        <form
          className="rn-col"
          style={{ gap: 'var(--space-12)' }}
          onSubmit={(e) => {
            e.preventDefault();
            open(typed);
          }}
        >
          <SettingsField
            label="Response id"
            hint="The id of a submitted response — from an incident, a task, or the receipt shown when it was submitted."
          >
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.currentTarget.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              spellCheck={false}
            />
          </SettingsField>
          <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
            <button type="submit" className="rn-btn" disabled={!UUID.test(typed.trim())}>
              Open it
            </button>
          </div>
        </form>

        {/* The reader's OWN reach, from their OWN keys. It never claims which
            rows would answer — that is decided per row on the server and no
            client can compute it. */}
        {scope.kind === 'none' ? null : <ScopeNote scope={responseScopeSentence(scope)} />}
        {widenerNote === null ? null : (
          <p className="rn-muted" role="note">
            {widenerNote}
          </p>
        )}
      </SettingsCard>

      {phase.kind === 'idle' ? null : (
        <SettingsCard>
          {phase.kind === 'loading' ? <SkeletonSet surface="table" rows={6} /> : null}

          {phase.kind === 'refused' ? (
            // No retry. A permission denial is an answer.
            <Refused heading={RESPONSE_REFUSED_HEADING} message={RESPONSE_REFUSED_MESSAGE} />
          ) : null}

          {phase.kind === 'withheld-or-absent' ? (
            // ALSO no retry, and deliberately NOT `SuccessEmpty`: "there is
            // nothing" is one of the two worlds this covers and stating it
            // alone would be the conflation this screen exists to refuse.
            <Refused heading={WITHHELD_OR_ABSENT_HEADING} message={WITHHELD_OR_ABSENT_MESSAGE} />
          ) : null}

          {phase.kind === 'failed' ? (
            <LoadFailure
              error={phase.error}
              message="We couldn’t load that response just now. This is a failure to ask, not an answer — do not read it as the response being missing or withheld."
              onRetry={() => void detail.refetch()}
              mainRead
            />
          ) : null}

          {phase.kind === 'ready' && detail.data ? <ResponseBody detail={detail.data} /> : null}
        </SettingsCard>
      )}
    </div>
  );
}

/** The response itself, once it has answered. */
function ResponseBody({ detail }: { detail: forms.FormResponseDetail }) {
  const { response, version, answers, emissions } = detail;
  const currency = versionCurrency(version);
  const rows = answersAgainstVersion(version, answers);
  const attestation = attestationReading(version, response);

  const columns: Column<AnswerRow>[] = [
    {
      key: 'question',
      header: 'Question',
      render: (row) => (
        <span>
          {row.label}
          <br />
          <span className="rn-muted">
            {row.sectionTitle} · {row.fieldKey}
            {row.required ? ' · required' : ''}
          </span>
        </span>
      ),
    },
    {
      key: 'answer',
      header: 'Answer',
      render: (row) =>
        row.answer === undefined ? (
          // A question that was asked and not answered is a fact about the
          // evidence. Filtering these rows out would delete it.
          <span className="rn-muted">Not answered</span>
        ) : (
          <span>{answerText(row.answer)}</span>
        ),
    },
    {
      key: 'note',
      header: '',
      render: (row) =>
        row.unknownField ? (
          <Pill variant="look" dot>
            Not a question in this version
          </Pill>
        ) : null,
    },
  ];

  return (
    <div className="rn-col" style={{ gap: 'var(--space-16)' }}>
      <div className="cmp-control-head">
        <h2 className="rn-card-title">
          {INSTRUMENT_READING[response.instrumentKind]} · v{version.versionNumber}
        </h2>
        <Pill variant={response.status === 'submitted' ? 'good' : 'plain'} dot>
          {response.status === 'submitted'
            ? 'Submitted'
            : response.status === 'in_progress'
              ? 'In progress'
              : 'Voided'}
        </Pill>
      </div>

      {/* THE SENTENCE THIS SCREEN IS FOR. Which version these answers were
          given against, and what has happened to that version since. This
          screen never asks what the form's CURRENT version is: that number
          would be true about the form and false about this evidence. */}
      <p className="rn-muted">{versionCurrencySentence(currency)}</p>

      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--space-8)' }}>
        <dt className="rn-muted">About</dt>
        <dd>{SUBJECT_READING[response.subjectKind]}</dd>
        <dt className="rn-muted">Submitted by</dt>
        <dd>
          {response.submittedBy === null ? (
            <span className="rn-muted">Not recorded</span>
          ) : (
            response.submittedBy.displayName
          )}
        </dd>
        <dt className="rn-muted">Submitted</dt>
        <dd>{formatWhen(response.submittedAt)}</dd>
      </dl>

      <div>
        <h3 className="rn-card-title">Attestation</h3>
        <p className="rn-muted">
          {attestation.kind === 'not-required'
            ? 'This version did not ask for an attestation.'
            : attestation.kind === 'missing'
              ? 'This version requires an attestation and none is recorded against this response. That is a gap in the evidence, not a rendering problem.'
              : `Attested by ${attestation.by}${attestation.at === null ? '' : ` on ${formatWhen(attestation.at)}`}.`}
        </p>
        {attestation.kind === 'given' && attestation.statement !== null ? (
          <p>{attestation.statement}</p>
        ) : null}
      </div>

      <div>
        <h3 className="rn-card-title">Answers</h3>
        <p className="rn-muted">
          In the order v{version.versionNumber} asked them, with the labels v{version.versionNumber}{' '}
          used.
        </p>
        <TableCard
          columns={columns}
          rows={rows}
          rowKey={(row) => row.fieldKey}
          caption={`Answers to v${version.versionNumber}`}
        />
      </div>

      <div>
        <h3 className="rn-card-title">What this raised</h3>
        {emissions.length === 0 ? (
          <p className="rn-muted">
            Nothing. Either no answer matched an approved signal mapping, or a mapping matched and
            has not been approved — an unapproved mapping is inert and emits nothing.
          </p>
        ) : (
          <ul className="rn-col" style={{ gap: 'var(--space-8)', paddingLeft: 'var(--space-16)' }}>
            {emissions.map((emission) => (
              <li key={emission.id}>
                <strong>{emission.signalKey}</strong> · {emission.signalState} · from{' '}
                {emission.fieldKey} · {formatWhen(emission.emittedAt)}
                <br />
                <span className="rn-muted">{emission.answerSummary}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
